import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getWindowSafe } from '@change-corgi/core/window';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';

import type { WatermarkPlacement } from '../../components/WatermarkedImage';

/** Matches the tailwind `md` breakpoint that the hero layout switches on. */
export const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

// The AI-Badge spec, translated for baking. The mark scales with the image, so its size is the
// spec's px over the hero's design width (21px at a 390px-wide mobile hero → 5.5%; 42px at the
// 1015px desktop hero → 4%). The padding mirrors the page's fixed text/content padding, so it
// stays in CSS px and is converted at composite time via the measured rendered width. Both
// viewports use top-left (the spec's fallback when the right corners are occupied): the sign
// panel covers the hero's top-right on desktop, and the device-gated NativeShareMobile button
// sticks to the top-right on mobile devices regardless of viewport width.
const MOBILE_PLACEMENT: WatermarkPlacement = { corner: 'top-left', markWidthRatio: 0.055, paddingCss: 16 };
const DESKTOP_PLACEMENT: WatermarkPlacement = { corner: 'top-left', markWidthRatio: 0.04, paddingCss: 20 };

function pickPlacement(): WatermarkPlacement {
	const matches = getWindowSafe()?.matchMedia(MOBILE_MEDIA_QUERY).matches ?? false;
	return matches ? MOBILE_PLACEMENT : DESKTOP_PLACEMENT;
}

type Result = ModelHookResult<
	{
		/** The viewer's arm bakes the watermark into the hero. */
		watermarked: boolean;
		/**
		 * Corner and geometry for the baked mark, picked once at mount for the current viewport —
		 * deliberately without a media-query listener: crossing the breakpoint later keeps showing
		 * the already-baked image (always watermarked, just placed for the other layout) until the
		 * next page load.
		 */
		placement: WatermarkPlacement;
	},
	{
		/**
		 * Reports a failed watermark composite (the unmarked original was shown instead), so the
		 * dilution of the treatment arm is measurable — see the treat effect for why failures
		 * still count as treated.
		 */
		onCompositeError: () => void;
	}
>;

export function useAiHeroPhoto(rootRef: RefObject<HTMLElement | null>): Result {
	const utilityContext = useUtilityContext();
	const track = useTracking();
	const {
		data: { experiments },
	} = usePetitionDetailsPageUserData();
	const experiment = experiments.aiHeroImageWatermarkExperiment;
	// Ref, not state: guards the treat across re-renders without re-running the effect
	const hasTreatedRef = useRef(false);
	const [placement] = useState(pickPlacement);

	// a fallback resolution (FCM off, not-found, ...) is not a real assignment: treating would
	// enroll the user server-side (the service treats by name and knows nothing about our FCM)
	// while we rendered them the default experience
	const treatable = !experiment.fallback;

	// Intent-to-treat bucketing: treat on the render decision (a real assignment), never on the
	// hero painting — only the treatment arm's render can fail, so a paint gate would drop users
	// asymmetrically. Composite failures stay in the measured population; onCompositeError keeps
	// their rate observable.
	useEffect(() => {
		// oxlint-disable-next-line react-hooks/refs
		if (!treatable || hasTreatedRef.current) return;
		// Both heading slots (desktop + mobile) mount their own AiHeroPhoto and CSS hides the
		// inactive one — only the instance the viewer can actually see records the treatment;
		// offsetParent is null inside any display:none ancestor. Safe to read here: effects run
		// after commit, and the offsetParent query forces the pending layout itself.
		// oxlint-disable-next-line react-hooks/refs
		if (!rootRef.current || rootRef.current.offsetParent === null) return;
		hasTreatedRef.current = true;
		void treatExperiment(utilityContext, experiment.experimentName);
	}, [treatable, experiment.experimentName, utilityContext, rootRef]);

	const onCompositeError = useCallback(() => {
		// petition_id comes from the page-level TrackingContext; the arm comes from the default
		// experiment_variations property (and only the enabled arm composites at all)
		void track('ai_hero_watermark_composite_failure');
	}, [track]);

	return {
		data: {
			// a fallback resolution always carries the default (control) variation, so no extra check
			watermarked: experiment.variation === 'enabled',
			placement,
		},
		actions: { onCompositeError },
	};
}
